import { Button, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation, useParams } from 'react-router-dom';

import { AREAS, areaFromPathname, areaHref, projectBasePath } from './projectAreas';

import { MaterialSymbol } from '@/components/MaterialSymbol';

/**
 * The project's area navigation: Planning, Financials, Team, Billing. No
 * settings gear any more: Project details is reached from the header's second
 * line (docs/briefs/project-details-v2/brief.md).
 *
 * These are LINKS, not a segmented control — real hrefs, so middle-click and
 * open-in-new-tab work, and `aria-current="page"` rather than `aria-pressed`.
 * That is the one thing separating it from `planning/PlanViewSwitcher`, which
 * is a genuine toggle over local state; `aria-pressed` on an anchor would be
 * wrong ARIA, and the theme's `contained secondary` variant repaints itself
 * blue on `[aria-pressed="true"]` anyway (`theme/components.ts:91`).
 *
 * It is also deliberately NOT the view switcher's sunken-track figure/ground.
 * On Planning the two sit about 40px apart, and two identical segmented
 * controls stacked read as one control with seven options. Navigation gets the
 * filled pill; the view toggle keeps the track.
 *
 * Both colours are themed variants, so there is no `sx` colour here for
 * `theme/designSystem.unit.ts` to reject.
 */
export const ProjectAreaNav = () => {
  const { t } = useTranslation(['pmShell', 'projectManagement']);
  const { pathname } = useLocation();
  const { projectId } = useParams<{ projectId?: string }>();

  const base = projectBasePath({ projectId });
  if (base === null) return null;

  const current = areaFromPathname(pathname);

  return (
    <Stack
      component="nav"
      aria-label={t('projectAreaNav.Label')}
      direction="row"
      data-automation-id="project-area-nav"
      sx={{
        alignItems: 'center',
        gap: 0.5,
        // The GROUP never wraps: a pill group that breaks its own line reads as
        // broken. The row around it wraps instead, so the breadcrumb that lands
        // in the leading slot later is what drops away first.
        flexWrap: 'nowrap',
        flexShrink: 0,
      }}
    >
      {AREAS.map(({ area, labelKey, icon, automationId }) => {
        const selected = current === area;
        return (
          <Button
            key={area}
            component={NavLink}
            to={areaHref(base, area)}
            size="small"
            variant={selected ? 'contained' : 'text'}
            color="secondary"
            aria-current={selected ? 'page' : undefined}
            data-automation-id={automationId}
            data-selected={selected}
            startIcon={<MaterialSymbol name={icon} size={16} />}
            sx={{
              whiteSpace: 'nowrap',
              /*
                The design system's hover, not MUI's. A text Button's default
                hover is a 4%-opacity black wash, which against the panel's
                near-white is almost nothing — the row read as inert.
                `--bg-hover` (fog-20) is what every other navigable row in the
                app uses, the sidebar's included, so the nav now answers the
                pointer the same way.
                The selected pill keeps the theme's own `contained secondary`
                hover; it is already filled and has nowhere lighter to go.
              */
              ...(selected ? {} : { '&:hover': { bgcolor: 'var(--bg-hover)' } }),
            }}
          >
            {t(labelKey)}
          </Button>
        );
      })}
    </Stack>
  );
};
